import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Col, Container, Row, Button } from 'react-bootstrap';
import { Card, CardBody } from '@/shared/components/Card';
import { LoadingSpinner } from '@/shared/components/Loading';
import { SubscriptionPlans } from '@/constants';
import User, { CRM_TYPES, Roles, UserRoles } from '@/models/user';
import { useUser, updateUser, deleteUser } from '@/services/users';
import { formatDate } from '@/utils/helpers';
import { getUserDisplayName } from '@/utils/user-display';
import ShipmentCard from './shipment-card';
import Field from './field';
import { errorBannerStyle, successBannerStyle, warningBannerStyle } from '@/shared/dark-inline-styles';

/**
 * The single user record (spec v2 §04).
 *
 * This REPLACES the old five-field edit modal rather than sitting beside it —
 * two edit surfaces for one record is how they drift apart. Everything on this
 * page comes from ONE request (`GET /admin/users/:id`), which returns the user,
 * their orders and their Telegram community state together.
 *
 * The save discipline is inherited from the modal and is load-bearing: only
 * fields the admin actually CHANGED are sent. Blindly re-writing every field
 * lets an unrelated edit revoke Course-33 access (`course33AccessExpiresAt`) or
 * clobber a value a Stripe/IAP webhook wrote after the page loaded.
 *
 * PII warning (spec v2 §07 #6): this view holds full name, phone and home
 * address. It is gated by `checkAdminAuthorization` server-side. Do not log any
 * of these fields, and do not add them to any analytics call.
 */

type Draft = {
    role: UserRoles;
    subscriptionPlan: SubscriptionPlans;
    onBoarded: boolean;
    personaOnboarded: boolean;
    course33Access: boolean;
    crmType: string;
    cohort: string;
    referrerName: string;
    linkedinUrl: string;
    testimonialVideo: boolean;
    testimonialPost: boolean;
    adminNotes: string;
    telegramHandle: string;
    telegramGroupJoined: boolean;
};

const buildDraft = (user: User): Draft => ({
    role: user.role,
    subscriptionPlan: user.subscriptionPlan as SubscriptionPlans,
    onBoarded: !!user.onBoarded,
    personaOnboarded: !!user.personaOnboarded,
    // Course 33 is a standalone product, not Premium. The stored value is an
    // expiry timestamp; the toggle is the derived "does it grant access now".
    course33Access: (user.course33AccessExpiresAt ?? 0) > Date.now(),
    crmType: user.crmType ?? '',
    cohort: user.cohort ?? '',
    referrerName: user.referrerName ?? '',
    linkedinUrl: user.linkedinUrl ?? '',
    testimonialVideo: !!user.testimonialVideo,
    testimonialPost: !!user.testimonialPost,
    adminNotes: user.adminNotes ?? '',
    telegramHandle: user.telegramHandle ?? '',
    telegramGroupJoined: !!user.telegramGroupJoined,
});

const UserProfile = () => {
    const { userId = '' } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const { data: response, isLoading, refetch } = useUser(userId);

    const detail = response?.data;
    const user = detail?.user;

    const [draft, setDraft] = useState<Draft | null>(null);
    const [initial, setInitial] = useState<Draft | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (!user) return;
        const next = buildDraft(user);
        setDraft(next);
        setInitial(next);
    }, [user]);

    const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
        setSaved(false);
        setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
    };

    // Only the fields that actually changed. See the class comment above — this
    // is the difference between "saved a note" and "silently revoked access".
    const changes = useMemo(() => {
        if (!draft || !initial) return {};
        const out: Parameters<typeof updateUser>[1] = {};
        (Object.keys(draft) as (keyof Draft)[]).forEach((key) => {
            if (draft[key] === initial[key]) return;
            if (key === 'crmType') {
                out.crmType = (draft.crmType || '') as User['crmType'];
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (out as any)[key] = draft[key];
        });
        return out;
    }, [draft, initial]);

    const isDirty = Object.keys(changes).length > 0;

    const handleSave = async () => {
        if (!isDirty) return;
        setError(null);
        setIsSaving(true);
        try {
            // `callApi` never throws on an API error — it resolves
            // { status: 'error' } — so a failed save would otherwise look
            // exactly like a successful one.
            const result = await updateUser(userId, changes);
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to update user.');
                return;
            }
            setSaved(true);
            await refetch();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to update user.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Delete this user? This soft-deletes the record.')) return;
        setError(null);
        setIsDeleting(true);
        try {
            const result = await deleteUser(userId);
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to delete user.');
                return;
            }
            navigate('/users');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to delete user.');
        } finally {
            setIsDeleting(false);
        }
    };

    if (isLoading || !user || !draft) {
        return (
            <Container>
                <div className="d-flex justify-content-center py-5">{isLoading ? <LoadingSpinner /> : <span>User not found.</span>}</div>
            </Container>
        );
    }

    const stripeUrl = user.stripeCustomerId ? `https://dashboard.stripe.com/customers/${user.stripeCustomerId}` : undefined;

    return (
        <Container>
            <Row>
                <Col md={12} className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    {/* `min-width: 0` for the same reason as in `field.tsx`: a flex
                        item will not shrink below its content by default, so a long
                        email pushes straight out of the page rather than wrapping. */}
                    <div style={{ minWidth: 0 }}>
                        <h3 className="page-title mb-1 text-break">{getUserDisplayName(user)}</h3>
                        <h3 className="page-subhead subhead mb-0 text-break">{user.email}</h3>
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" onClick={() => navigate('/users')}>
                            Back to Users
                        </Button>
                        <Button variant="primary" disabled={!isDirty || isSaving} onClick={handleSave}>
                            {isSaving ? 'Saving…' : isDirty ? 'Save changes' : 'Saved'}
                        </Button>
                    </div>
                </Col>
            </Row>

            {error && (
                <Row>
                    <Col md={12}>
                        <div style={errorBannerStyle}>{error}</div>
                    </Col>
                </Row>
            )}
            {saved && !isDirty && (
                <Row>
                    <Col md={12}>
                        <div style={successBannerStyle}>Changes saved.</div>
                    </Col>
                </Row>
            )}

            {/* Non-blocking. Spec v2 §07 #5: several orders per user is
                legitimate, so "one board per email" is a prompt to look, never a
                write-time block. */}
            {(detail?.warnings ?? []).map((warning) => (
                <Row key={warning}>
                    <Col md={12}>
                        <div style={warningBannerStyle}>⚠ {warning}</div>
                    </Col>
                </Row>
            ))}

            <Row>
                <Col md={6}>
                    <Card>
                        <CardBody>
                            <h4 className="mb-4">Identity</h4>
                            <Field label="Name" value={getUserDisplayName(user)} />
                            <Field label="Email" value={user.email} />
                            <Field label="Phone" value={user.phone} />
                            <Field label="User id" value={user.id} mono />
                            <Field label="Created" value={formatDate(user.created_at)} />
                            <Field label="Source" value={user.signupSource} />

                            <hr />
                            <h5 className="mb-3">CRM</h5>
                            <div className="mb-3">
                                <label className="form-label" htmlFor="crmType">
                                    Type
                                </label>
                                <select id="crmType" className="form-select" value={draft.crmType} onChange={(e) => set('crmType', e.target.value)}>
                                    {/* Nullable on purpose — most users have no type and that is correct. */}
                                    <option value="">—</option>
                                    {CRM_TYPES.map((t) => (
                                        <option key={t} value={t} className="text-capitalize">
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label" htmlFor="cohort">
                                    Cohort
                                </label>
                                <input
                                    id="cohort"
                                    className="form-control"
                                    value={draft.cohort}
                                    onChange={(e) => set('cohort', e.target.value)}
                                    placeholder="e.g. 2"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label" htmlFor="referrerName">
                                    Referrer
                                </label>
                                <input
                                    id="referrerName"
                                    className="form-control"
                                    value={draft.referrerName}
                                    onChange={(e) => set('referrerName', e.target.value)}
                                    placeholder="Person or KOL who brought them in"
                                />
                                {/* Not the referral programme: that pays real commission
                                    and is keyed on referredByCode, which this never touches. */}
                                <small className="text-muted">A note, not the referral programme.</small>
                            </div>
                            <div className="mb-3">
                                <label className="form-label" htmlFor="linkedinUrl">
                                    LinkedIn
                                </label>
                                <input
                                    id="linkedinUrl"
                                    className="form-control"
                                    value={draft.linkedinUrl}
                                    onChange={(e) => set('linkedinUrl', e.target.value)}
                                    placeholder="https://www.linkedin.com/in/…"
                                />
                                {draft.linkedinUrl && (
                                    <a href={draft.linkedinUrl} target="_blank" rel="noreferrer noopener">
                                        Open profile ↗
                                    </a>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card>
                        <CardBody>
                            <h4 className="mb-4">Access &amp; billing</h4>
                            <div className="mb-3">
                                <label className="form-label" htmlFor="role">
                                    Role
                                </label>
                                <select
                                    id="role"
                                    className="form-select"
                                    value={draft.role}
                                    onChange={(e) => set('role', e.target.value as UserRoles)}
                                >
                                    <option value={Roles.Member}>{Roles.Member}</option>
                                    <option value={Roles.Admin}>{Roles.Admin}</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label" htmlFor="subscriptionPlan">
                                    Subscription
                                </label>
                                <select
                                    id="subscriptionPlan"
                                    className="form-select"
                                    value={draft.subscriptionPlan}
                                    onChange={(e) => set('subscriptionPlan', e.target.value as SubscriptionPlans)}
                                >
                                    <option value={SubscriptionPlans.Free}>{SubscriptionPlans.Free}</option>
                                    <option value={SubscriptionPlans.Premium}>{SubscriptionPlans.Premium}</option>
                                </select>
                            </div>
                            <Field label="Sub status" value={user.subscriptionStatus} />
                            <div className="mb-3">
                                <label className="form-label" htmlFor="course33Access">
                                    Course 33 access
                                </label>
                                <select
                                    id="course33Access"
                                    className="form-select"
                                    value={draft.course33Access ? 1 : 0}
                                    onChange={(e) => set('course33Access', e.target.value === '1')}
                                >
                                    <option value={0}>Off</option>
                                    <option value={1}>On (grants ~1-year access)</option>
                                </select>
                            </div>
                            <Field
                                label="C33 expires"
                                value={user.course33AccessExpiresAt ? new Date(user.course33AccessExpiresAt).toISOString().slice(0, 10) : undefined}
                            />
                            {/* Read-only: Stripe owns promo codes (spec v2 §00). */}
                            <Field label="Promo used" value={user.redeemedPromotionCode} />
                            <div className="mb-3">
                                <label className="form-label">Stripe</label>
                                <div>
                                    {stripeUrl ? (
                                        <a href={stripeUrl} target="_blank" rel="noreferrer noopener">
                                            Open customer ↗
                                        </a>
                                    ) : (
                                        <span className="text-muted">—</span>
                                    )}
                                </div>
                            </div>
                            <div className="mb-3">
                                <label className="form-label" htmlFor="onBoarded">
                                    OnBoarded
                                </label>
                                <select
                                    id="onBoarded"
                                    className="form-select"
                                    value={draft.onBoarded ? 1 : 0}
                                    onChange={(e) => set('onBoarded', e.target.value === '1')}
                                >
                                    <option value={0}>false</option>
                                    <option value={1}>true</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label" htmlFor="personaOnboarded">
                                    Persona onboarded
                                </label>
                                <select
                                    id="personaOnboarded"
                                    className="form-select"
                                    value={draft.personaOnboarded ? 1 : 0}
                                    onChange={(e) => set('personaOnboarded', e.target.value === '1')}
                                >
                                    <option value={0}>false</option>
                                    <option value={1}>true</option>
                                </select>
                            </div>
                            <Field label="Active" value={user.active ? 'Yes' : 'No'} />
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <ShipmentCard orders={detail?.orders ?? []} user={user} onSaved={refetch} />
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Card>
                        <CardBody>
                            <h4 className="mb-4">Community &amp; testimonials</h4>
                            {/* Editable: the bot only learns a handle if the member
                                happens to have a public username at the moment they
                                join, and plenty of clients were added by an admin who
                                simply knows theirs. A HANDLE is for humans to search
                                with — it is not how the bot messages anybody, which
                                needs the numeric id Telegram gives us at the join. */}
                            <div className="mb-3">
                                <label className="form-label" htmlFor="telegramHandle">
                                    TG handle
                                </label>
                                <input
                                    id="telegramHandle"
                                    className="form-control"
                                    value={draft.telegramHandle}
                                    onChange={(e) => set('telegramHandle', e.target.value.replace(/^@/, ''))}
                                    placeholder={detail?.telegram?.username ? detail.telegram.username : 'username (no @)'}
                                />
                                {detail?.telegram?.username && detail.telegram.username !== draft.telegramHandle && (
                                    <small className="d-block text-muted">Telegram reports @{detail.telegram.username}.</small>
                                )}
                            </div>
                            <Field
                                label="Invite sent"
                                value={detail?.telegram?.inviteSentAt ? formatDate(detail.telegram.inviteSentAt) : undefined}
                            />
                            <Field label="Joined at" value={detail?.telegram?.joinedAt ? formatDate(detail.telegram.joinedAt) : undefined} />
                            <div className="form-check mb-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="telegramGroupJoined"
                                    checked={draft.telegramGroupJoined}
                                    onChange={(e) => set('telegramGroupJoined', e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="telegramGroupJoined">
                                    Joined Telegram group
                                </label>
                                {/* The bot sets this automatically; the checkbox is the
                                    fallback for a join it could not attribute. */}
                                <small className="d-block text-muted">Auto-detected — tick to override.</small>
                            </div>
                            <div className="form-check mb-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="testimonialVideo"
                                    checked={draft.testimonialVideo}
                                    onChange={(e) => set('testimonialVideo', e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="testimonialVideo">
                                    Testimonial — video
                                </label>
                            </div>
                            <div className="form-check mb-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="testimonialPost"
                                    checked={draft.testimonialPost}
                                    onChange={(e) => set('testimonialPost', e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="testimonialPost">
                                    Testimonial — post
                                </label>
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card>
                        <CardBody>
                            <h4 className="mb-4">Notes</h4>
                            <textarea
                                className="form-control"
                                rows={8}
                                value={draft.adminNotes}
                                onChange={(e) => set('adminNotes', e.target.value)}
                                placeholder="Free text…"
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col md={12} className="d-flex justify-content-end pb-4">
                    <Button variant="outline-danger" disabled={isDeleting} onClick={handleDelete}>
                        {isDeleting ? 'Deleting…' : 'Delete user'}
                    </Button>
                </Col>
            </Row>
        </Container>
    );
};

export default UserProfile;
